// src/components/map/utils/baseWellTileErrors.ts
//
// One place that recognises a failed base-well vector tile.
//
// The base-well layer is declared for every org now (EVA-35), and eva-map-svc
// answers 503 + `Retry-After: 30` on both base-well tile endpoints until its
// attribute store finishes its first load (eva-map-svc#594) — seconds to a
// couple of minutes per pod. That is a normal rollout state, not a bug, but
// mapbox-gl raises one `error` event per failed tile fetch, so without this
// module a single viewport of ~20 tiles becomes ~20 error-level Sentry
// exceptions per pan, per user.
//
// The base layer now posts its own filter and references it by id (EVA-35).
// eva-map-svc answers `410 Gone` on either base-well endpoint when that
// filterId has expired or is unknown to data-svc — the client's job is to
// re-post the filter and swap in the new id, bounded by a cooldown window
// (see useBaseLayerFilterSync's FILTER_EXPIRY_RETRY_COOLDOWN_MS) rather than
// once per occurrence — a repost's own 200 doesn't prove the new filterId
// will actually serve tiles, so bounding on "did the last attempt succeed" is
// not enough; see that hook's own doc comment for the full timeline.
//
// Three consumers share this detection so the event is only classified once:
//   - useMapLifecycle's map `error` handlers report it (throttled, `warning`)
//   - MapboxWrapper's retry effect subscribes to the 503 case and refetches
//   - useBaseLayerFilterSync subscribes to the 410 case and re-posts the
//     filter (this is a mapbox tile 410 only — the resource-play dropdown's
//     own request no longer carries this filterId at all, so it can't 410
//     for this reason any more; see fetchBaseWellPlays in api/map.ts)
import * as Sentry from "@sentry/react";

import { BASE_WELL_LAYER, BASE_WELL_RASTER_LAYER } from "constants/mapLayers.constants";

/**
 * The base-well vector source id. Both base-well MVT layers (the survey lines
 * and the well circles) read from this one source — see MapboxWrapper's
 * base-well effect, which builds the same id from the same layer constant.
 */
export const BASE_WELL_SOURCE_ID = `${BASE_WELL_LAYER}-lines`;

/**
 * The base-well raster source id (EVA-35 raster crossover) — same id as its
 * layer, matching the interactive well layer's HZ_WELL_RASTER_LAYER pattern.
 */
export const BASE_WELL_RASTER_SOURCE_ID = BASE_WELL_RASTER_LAYER;

/** Legacy GeoServer WMS source ("Base Layer Refactor" off) — same id as its layer. */
export const BASE_WELL_LEGACY_SOURCE_ID = BASE_WELL_LAYER;

/** What eva-map-svc answers while its attribute store is still loading. */
const BASE_WELL_NOT_READY_STATUS = 503;

/** What eva-map-svc answers when the base layer's filterId has expired or is unknown. */
const BASE_WELL_FILTER_EXPIRED_STATUS = 410;

/**
 * One Sentry event per source per window. A pan fetches ~20 tiles, and each
 * source feeds its own mapbox source cache, so a single not-ready pan can
 * raise dozens of `error` events across the MVT and raster sources. The
 * window matches the server's own `Retry-After: 30`, so a persistent outage
 * still reports roughly once per retry cycle instead of once per tile.
 */
const BASE_WELL_ERROR_THROTTLE_MS = 30_000;

/**
 * How long to wait before refetching failed tiles, multiplied by the attempt
 * number — 30s, 60s, 90s. The base matches the server's `Retry-After: 30`.
 */
export const BASE_WELL_RETRY_BASE_MS = 30_000;

/**
 * Hard cap on automatic refetches per mounted map. With the backoff above that
 * covers ~3 minutes, which is the far end of eva-map-svc#594's stated
 * "seconds to a couple of minutes" first load. Past that the layer stays empty
 * until the user pans, zooms or reloads — the behaviour before this change.
 */
export const BASE_WELL_MAX_RETRIES = 3;

/** The parts of a mapbox `error` event this module reads. */
interface MapboxErrorEventLike {
  error?: { message?: string; name?: string; status?: number; url?: string };
  sourceId?: string;
}

interface BaseWellTileError {
  message?: string;
  sourceId: string;
  /** HTTP status from mapbox-gl's AJAXError, when the failure was a response. */
  status?: number;
  url?: string;
}

// Throttle bookkeeping, keyed by source id. Module-level because the map's
// error handlers are re-registered on every style reload and must not reset
// the window — see resetBaseWellTileErrorState for the test seam.
interface ThrottleState {
  lastReportedAt: number;
  suppressed: number;
}
const throttleBySourceId = new Map<string, ThrottleState>();

type NotReadyListener = (matched: BaseWellTileError) => void;
const notReadyListeners = new Set<NotReadyListener>();

type FilterExpiredListener = () => void;
const filterExpiredListeners = new Set<FilterExpiredListener>();

/**
 * Recognises a mapbox `error` event raised by either base-well source — the
 * MVT source (survey lines + well circles) or the raster source (EVA-35
 * raster crossover, below HZ_RASTER_MVT_CROSSOVER_ZOOM). Returns undefined for
 * every other map error so the caller can fall through to its own reporting.
 */
function matchBaseWellTileError(event: unknown): BaseWellTileError | undefined {
  const e = event as MapboxErrorEventLike | undefined;
  if (
    !e ||
    (e.sourceId !== BASE_WELL_SOURCE_ID &&
      e.sourceId !== BASE_WELL_RASTER_SOURCE_ID &&
      e.sourceId !== BASE_WELL_LEGACY_SOURCE_ID)
  ) {
    return undefined;
  }

  return {
    message: e.error?.message,
    sourceId: e.sourceId,
    status: e.error?.status,
    url: e.error?.url
  };
}

/**
 * Reports a base-well tile failure to Sentry at `warning`, at most once per
 * throttle window per source. Mirrors the `map_raster_tile` context shape used
 * for raster tiles: `status` is the "failed" state, `httpStatus` is the code.
 *
 * Every base-well tile failure is downgraded, not just the 503 — a single
 * tile fetch failing is never actionable as an exception, and the flood is the
 * same size whatever the status. The status is carried in the context so a 500
 * is still distinguishable from the expected not-ready 503.
 */
function reportBaseWellTileError(matched: BaseWellTileError): void {
  const now = Date.now();
  const previous = throttleBySourceId.get(matched.sourceId);
  if (previous && now - previous.lastReportedAt < BASE_WELL_ERROR_THROTTLE_MS) {
    previous.suppressed += 1;
    return;
  }

  // Carry the count of events the last window swallowed so a throttled report
  // doesn't understate the size of the outage.
  const suppressed = previous?.suppressed ?? 0;
  throttleBySourceId.set(matched.sourceId, { lastReportedAt: now, suppressed: 0 });

  Sentry.withScope((scope) => {
    scope.setContext("map_base_well_tile", {
      action: "tile_load",
      errorMessage: matched.message,
      httpStatus: matched.status,
      sourceId: matched.sourceId,
      status: "failed",
      suppressedSinceLastReport: suppressed,
      url: matched.url
    });
    scope.setLevel("warning");
    Sentry.captureMessage(
      `Map base well tile failed to load from source "${matched.sourceId}"` +
        (matched.status ? ` (HTTP ${matched.status})` : "") +
        (matched.message ? `: ${matched.message}` : "")
    );
  });
}

/**
 * Subscribes to the "server says it isn't ready yet" case (503). Returns an
 * unsubscribe function. Listeners are called once per failed tile, so a
 * listener must be idempotent.
 */
export function onBaseWellTileNotReady(listener: NotReadyListener): () => void {
  notReadyListeners.add(listener);
  return () => {
    notReadyListeners.delete(listener);
  };
}

/**
 * Subscribes to the "the base layer's filterId has expired or is unknown"
 * case (410). Returns an unsubscribe function. Fired at most once per failed
 * tile — useBaseLayerFilterSync is responsible for bounding how many times it
 * actually reposts in response (never an unbounded loop).
 */
export function onBaseWellFilterExpired(listener: FilterExpiredListener): () => void {
  filterExpiredListeners.add(listener);
  return () => {
    filterExpiredListeners.delete(listener);
  };
}

/**
 * Notifies every `onBaseWellFilterExpired` subscriber. Exported so tests can
 * simulate the mapbox-`error`-event path this is otherwise only reached
 * through (`handleBaseWellTileError`).
 */
export function notifyBaseWellFilterExpired(): void {
  // Copy first: a listener that unsubscribes itself must not mutate the set
  // mid-iteration.
  for (const listener of [...filterExpiredListeners]) listener();
}

/**
 * The single entry point for the map's `error` handlers. Returns true when the
 * event was a base-well tile failure and has been handled — the caller must
 * then NOT fall through to its generic error-level `captureException`.
 *
 * The map carries two `error` listeners (one registered at creation, one in
 * the lifecycle effect), so this runs twice per event; the throttle collapses
 * the duplicate report and listeners are required to be idempotent.
 */
export function handleBaseWellTileError(event: unknown): boolean {
  const matched = matchBaseWellTileError(event);
  if (!matched) return false;

  reportBaseWellTileError(matched);

  if (matched.status === BASE_WELL_NOT_READY_STATUS) {
    // Copy first: a listener that unsubscribes itself must not mutate the set
    // mid-iteration.
    for (const listener of [...notReadyListeners]) listener(matched);
  }

  if (matched.status === BASE_WELL_FILTER_EXPIRED_STATUS) {
    notifyBaseWellFilterExpired();
  }

  return true;
}

/** Test seam — clears the module-level throttle window and listeners. */
export function resetBaseWellTileErrorState(): void {
  throttleBySourceId.clear();
  notReadyListeners.clear();
  filterExpiredListeners.clear();
}
